import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled && !mobileOpen;

  const navLinks = [
    { to: '/', label: t('navHome') },
    { to: '/category/star-scandal', label: t('navStarScandal') },
    { to: '/category/influencer-drama', label: t('navInfluencer') },
    { to: '/category/daily-competition', label: t('navCompetition') },
    { to: '/about', label: t('navAbout') },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md'
      }`}
    >
      <div className="px-4 md:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className={`text-2xl font-heading font-bold transition-colors duration-300 ${
            isTransparent ? 'text-background-50' : 'text-primary-600'
          }`}>
            吃瓜网
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer relative group ${
                isTransparent
                  ? 'text-background-50/90 hover:text-background-50'
                  : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              {link.label}
              <span
                className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-300 ${
                  isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                } ${
                  isTransparent ? 'bg-background-50' : 'bg-primary-500'
                }`}
              />
            </Link>
          ))}
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
            isTransparent ? 'text-background-50' : 'text-foreground-800'
          }`}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium px-3 py-2 rounded-md transition-colors cursor-pointer ${
                  isActive(link.to)
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}